import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LanguageToggle from '../components/LanguageToggle'
import { supabase } from '../lib/supabase'
import { getPasswordResetRedirectUrl } from '../lib/authRedirects'
import interactiveBusinessLogo from '../Logo/InteractiveBusinessLogo.svg'

const ForgotPasswordPage = () => {
  const { t, i18n } = useTranslation('auth')
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [requestSubmitted, setRequestSubmitted] = useState(false)
  const redirectTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
      }
    }
  }, [])

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')
    setRequestSubmitted(false)

    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail) {
      setError(t('validation.emailRequired'))
      return
    }

    setLoading(true)
    try {
      const passwordResetRedirectTo = getPasswordResetRedirectUrl()
      const { data, error: invokeError } = await supabase.functions.invoke('forgot-password', {
        body: {
          email: normalizedEmail,
          locale: i18n.language,
          redirectTo: passwordResetRedirectTo,
        },
      })

      if (invokeError) {
        throw invokeError
      }

      if (data?.ok !== true) {
        throw new Error('Invalid forgot-password response')
      }

      setRequestSubmitted(true)
      setEmail('')

      redirectTimeoutRef.current = setTimeout(() => {
        navigate('/login', {
          replace: true,
        })
      }, 1800)
    } catch (requestError) {
      console.error('Failed to request password reset:', requestError)
      setError(t('forgotPasswordPage.requestError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-3 flex flex-col justify-center sm:flex sm:items-center sm:justify-center sm:px-6 sm:py-8">
      <div className="mx-auto flex w-full max-w-xl flex-col sm:max-w-md">
        <div className="mb-5 flex items-start justify-between gap-3 sm:hidden">
          <img
            src={interactiveBusinessLogo}
            alt="Interactive Business logo"
            className="h-10 w-auto"
          />

          <div className="flex items-center gap-2">
            <Link
              to="/about"
              className="text-sm font-medium text-gray-700 transition-colors hover:text-[#FF1B54]"
            >
              About
            </Link>
            <LanguageToggle />
          </div>
        </div>

        <div className="hidden sm:block absolute top-6 left-6">
          <img
            src={interactiveBusinessLogo}
            alt="Interactive Business logo"
            className="h-12 w-auto"
          />
        </div>

        <div className="hidden sm:flex absolute top-4 right-4 items-center gap-4">
          <Link
            to="/about"
            className="text-base font-medium text-gray-700 transition-colors hover:text-[#FF1B54]"
          >
            About
          </Link>
          <LanguageToggle />
        </div>

        <div className="w-full space-y-5 sm:space-y-10">
          <div>
            <h2 className="text-center text-3xl font-extrabold leading-tight text-gray-900 sm:mt-6 sm:text-4xl">
              <span className="text-[#FF1B54]">Interactive </span>
              <span className="text-gray-900">Business</span>
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 sm:mt-3 sm:text-base">
              {t('forgotPasswordPage.subtitle')}
            </p>
          </div>

          <div className="rounded-lg bg-white p-6 shadow sm:p-8">
            <h1 className="text-xl font-semibold text-gray-900">{t('forgotPasswordPage.title')}</h1>

            <p className="mt-2 text-sm text-gray-600">{t('forgotPasswordPage.description')}</p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-6">
              {error && (
                <div className="rounded bg-red-50 border border-red-200 px-4 py-3 text-red-700">
                  {error}
                </div>
              )}

              {requestSubmitted && (
                <div className="rounded bg-green-50 border border-green-200 px-4 py-3 text-green-700">
                  <p className="font-medium">{t('forgotPasswordPage.successTitle')}</p>
                  <p className="mt-1 text-sm">{t('forgotPasswordPage.successMessage')}</p>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('forgotPasswordPage.emailLabel')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t('forgotPasswordPage.emailPlaceholder')}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54]"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center rounded-md border border-transparent bg-[#FF1B54] py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-[#E1174C] focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2 disabled:opacity-50"
              >
                {loading ? t('forgotPasswordPage.submitting') : t('forgotPasswordPage.submitButton')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-[#FF1B54] hover:text-[#E1174C]">
                {t('forgotPasswordPage.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
